import { useCallback, useEffect, useMemo, useState } from 'react';
import { Wordmark } from '@/kit';
import { checkPin, getConfiguredPin, unlockPin } from './pinGate';

/**
 * Soft-launch PIN wall — numeric keypad, auto-submit (mirrors HOTTR.tv).
 */
export function PinGateLanding({ onUnlocked }: { onUnlocked: () => void }) {
  const expected = useMemo(() => getConfiguredPin(), []);
  const pinLength = useMemo(() => {
    if (/^\d+$/.test(expected) && expected.length >= 4 && expected.length <= 12) {
      return expected.length;
    }
    return 6;
  }, [expected]);

  const [digits, setDigits] = useState<string[]>([]);
  const [error, setError] = useState(false);

  const tryUnlock = useCallback(
    (next: string[]) => {
      const pin = next.join('');
      if (checkPin(pin)) {
        unlockPin();
        onUnlocked();
        return;
      }
      setError(true);
      window.setTimeout(() => {
        setDigits([]);
        setError(false);
      }, 800);
    },
    [onUnlocked],
  );

  const handleDigit = useCallback(
    (d: string) => {
      setDigits((prev) => {
        if (prev.length >= pinLength) return prev;
        const next = [...prev, d];
        setError(false);
        if (next.length === pinLength) {
          queueMicrotask(() => tryUnlock(next));
        }
        return next;
      });
    },
    [pinLength, tryUnlock],
  );

  const handleDelete = useCallback(() => {
    setDigits((prev) => prev.slice(0, -1));
    setError(false);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleDigit(e.key);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleDelete();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleDigit, handleDelete]);

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[#0A0005] text-white">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="pin-orb pin-orb-a" />
        <div className="pin-orb pin-orb-b" />
        <div className="pin-orb pin-orb-c" />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,0,5,0.35) 0%, rgba(5,6,8,0.72) 50%, rgba(5,6,8,0.96) 100%)',
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-sm px-6">
        <div className="mb-8 flex flex-col items-center text-center">
          <Wordmark suffix="me" size="xl" />
          <p className="mt-6 text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
            Private preview
          </p>
          <p className="mx-auto mt-4 max-w-xs text-center text-sm text-white/72">
            Enter the access code to step in
            <span className="text-hottr-pink">.</span> Access lasts 24 hours, then you confirm
            again
            <span className="text-hottr-pink">.</span>
          </p>
        </div>

        <div className="space-y-6 rounded-sm border border-white/[0.12] bg-hottr-charcoal/90 p-7 backdrop-blur-md">
          <div
            className="flex justify-center gap-2"
            role="status"
            aria-label={`${digits.length} of ${pinLength} digits entered`}
          >
            {Array.from({ length: pinLength }).map((_, i) => (
              <div
                key={i}
                className={[
                  'flex h-12 w-9 items-center justify-center border text-lg font-display font-bold transition-all duration-200 rounded-sm',
                  error
                    ? 'border-hottr-pink/70 animate-[pin-shake_0.3s_ease-in-out]'
                    : digits[i]
                      ? 'border-hottr-pink/60 text-white shadow-neon-soft'
                      : 'border-white/[0.12]',
                ].join(' ')}
              >
                {digits[i] ? (
                  <span aria-hidden>•</span>
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
                )}
              </div>
            ))}
          </div>

          <div className="mx-auto grid max-w-[280px] grid-cols-3 gap-2.5">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'].map((key) => {
              if (key === '') return <div key="blank" />;
              if (key === 'del') {
                return (
                  <button
                    key="del"
                    type="button"
                    onClick={handleDelete}
                    className="flex h-14 items-center justify-center rounded-sm border border-white/[0.12] bg-white/[0.05] font-display text-[11px] font-bold uppercase tracking-widest text-white/48 transition-all duration-200 hover:border-white/25 hover:text-white active:scale-95"
                  >
                    Del
                  </button>
                );
              }
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleDigit(key)}
                  className="flex h-14 items-center justify-center rounded-sm border border-white/[0.12] bg-white/[0.05] font-display text-xl font-bold text-white transition-all duration-200 hover:border-hottr-pink/40 hover:bg-hottr-pink/10 active:scale-95 active:bg-hottr-pink/20"
                >
                  {key}
                </button>
              );
            })}
          </div>

          {error && (
            <p
              className="text-center text-[11px] font-bold uppercase tracking-widest text-hottr-pink"
              role="alert"
            >
              Invalid code
            </p>
          )}
        </div>

        <p className="mt-8 text-center text-[10px] font-extrabold uppercase tracking-hottr-stamp text-white/32">
          Soft launch · 24h access · Melbourne
        </p>
      </div>

      <style>{`
        .pin-orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(48px);
          will-change: transform, opacity;
        }
        .pin-orb-a {
          width: min(70vw, 520px);
          height: min(70vw, 520px);
          left: -12%;
          top: -8%;
          background: radial-gradient(circle, rgba(255,12,182,0.55) 0%, rgba(255,12,182,0) 70%);
          animation: pin-drift-a 32s ease-in-out infinite;
        }
        .pin-orb-b {
          width: min(80vw, 640px);
          height: min(80vw, 640px);
          right: -18%;
          top: 18%;
          background: radial-gradient(circle, rgba(196,0,111,0.5) 0%, rgba(196,0,111,0) 68%);
          animation: pin-drift-b 40s ease-in-out infinite;
        }
        .pin-orb-c {
          width: min(75vw, 560px);
          height: min(75vw, 560px);
          left: 10%;
          bottom: -20%;
          background: radial-gradient(circle, rgba(122,0,62,0.65) 0%, rgba(122,0,62,0) 70%);
          animation: pin-drift-c 36s ease-in-out infinite;
        }
        @keyframes pin-drift-a {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.85; }
          50% { transform: translate(12%, 18%) scale(1.12); opacity: 1; }
        }
        @keyframes pin-drift-b {
          0%, 100% { transform: translate(0, 0) scale(1.05); opacity: 0.75; }
          50% { transform: translate(-16%, 10%) scale(0.95); opacity: 0.95; }
        }
        @keyframes pin-drift-c {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.9; }
          50% { transform: translate(14%, -12%) scale(1.08); opacity: 0.7; }
        }
        @keyframes pin-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .pin-orb-a, .pin-orb-b, .pin-orb-c { animation: none; }
        }
      `}</style>
    </div>
  );
}
